import React, { useCallback, useMemo } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { handleTabListKeyDown } from 'utilities/tabKeyboardNavigation'

// Used by both the Activity sub-tabs (Needs your attention / Resolved) and
// the CaseDetail tabs (Overview / Conversation Transcript). Centralizes the
// full ARIA APG tab pattern — tablist + roving tabIndex + arrow-key nav —
// so the wiring stays consistent and isn't duplicated across scenes.
//
// Owns: the role="tablist" div, the per-tab buttons, the underline-on-active
// styling, the keyboard navigation hand-off to handleTabListKeyDown.
// Does NOT own: the tabpanel(s). Each consumer renders its own panel(s) and
// derives the active panel's aria-labelledby from the tab's tabId.

export type Tab<K extends string> = {
  key: K
  // DOM id for aria-controls on the button + aria-labelledby on the panel.
  tabId: string
  // DOM id for the corresponding tabpanel. Lets consumers compute the
  // tabpanel id without duplicating a key→id mapping outside this file.
  panelId: string
  label: React.ReactNode
  // Optional inline content rendered immediately after `label` (e.g.
  // Activity's needs-attention count pill). Kept generic so per-tab
  // affordances stay in the consumer's hands.
  badge?: React.ReactNode
}

type Props<K extends string> = {
  tabs: ReadonlyArray<Tab<K>>
  activeTab: K
  onTabChange: (tab: K) => void
  ariaLabel: string
}

function TabBar<K extends string>({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel,
}: Props<K>): React.ReactElement {
  const [css] = useStyletron()

  const tabOrder = useMemo(() => tabs.map(tab => tab.key), [tabs])
  const tabElementIds = useMemo(
    () =>
      tabs.reduce<Record<string, string>>((acc, tab) => {
        acc[tab.key] = tab.tabId
        return acc
      }, {}) as Record<K, string>,
    [tabs]
  )

  const tabClass = useCallback(
    (key: K) =>
      css({
        background: 'none',
        // Longhand, not `border: 'none'` — styletron-engine-atomic can't mix a
        // border shorthand with the borderBottom longhand in one rule; class
        // injection order between them is non-deterministic.
        borderTop: 'none',
        borderRight: 'none',
        borderLeft: 'none',
        borderBottom:
          activeTab === key
            ? `2px solid ${merchantTheme.colors.belizeHole50}`
            : '2px solid transparent',
        color:
          activeTab === key
            ? merchantTheme.colors.belizeHole50
            : merchantTheme.colors.grey70,
        fontWeight: 700,
        fontSize: '12px',
        letterSpacing: '2px',
        textTransform: 'uppercase' as const,
        padding: '12px 16px',
        cursor: 'pointer',
      }),
    [activeTab, css]
  )

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={event =>
        handleTabListKeyDown(event, {
          tabs: tabOrder,
          activeTab,
          setActiveTab: onTabChange,
          tabElementIds,
        })
      }
      className={css({
        borderBottom: `1px solid ${merchantTheme.colors.grey20}`,
      })}
    >
      {tabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          id={tab.tabId}
          aria-selected={activeTab === tab.key}
          // Consumers render a single panel div whose id swaps with the
          // active tab, so only the active tab has a matching DOM target —
          // an inactive tab's aria-controls would dangle. APG allows
          // aria-controls to be omitted when there's no element to point to.
          aria-controls={activeTab === tab.key ? tab.panelId : undefined}
          tabIndex={activeTab === tab.key ? 0 : -1}
          className={tabClass(tab.key)}
          onClick={() => onTabChange(tab.key)}
        >
          {tab.label}
          {tab.badge}
        </button>
      ))}
    </div>
  )
}

export default TabBar
